import { api, endpoints } from "api/api";
import { BaseApiPaging } from "api/types";
import { AxiosPromise } from "axios";
import { AuthorApi } from "./authorsApi";
import { LandingPageSectionApi, SectionFormValues } from "./shopApi";

// === Types ===

export interface BlogCategoryApi {
  id: number;
  slug: string;
  name: string;
}

export interface BlogPostListItemApi {
  id: number;
  slug: string;
  title: string;
  isPublished: boolean;
  publishedDate?: Date;
  author?: AuthorApi;
  coverImage?: string;
  coverImageAlt?: string;
  excerpt?: string;
  createdDate: Date;
  lastModified: Date;
  categories: BlogCategoryApi[];
}

export type BlogPostSectionApi = Omit<LandingPageSectionApi, "shopLandingPageId"> & {
  blogPostId: number;
};

export interface BlogPostApi extends BlogPostListItemApi {
  showTableOfContents: boolean;
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  shopId: number;
  sections?: BlogPostSectionApi[];
}

export interface BlogPostFormValues {
  title: string;
  slug: string;
  isPublished: boolean;
  publishedDate?: Date | string | null;
  authorId?: number | null;
  coverImage?: string;
  coverImageAlt?: string;
  excerpt?: string;
  showTableOfContents: boolean;
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  categoryIds: number[];
}

export interface BlogCategoryFormValues {
  name: string;
  slug?: string;
}

export interface BlogSettingsApi {
  title: string;
  basePath: string;
  metaDescription?: string;
  heroText?: string;
  heroImage?: string;
  heroBackgroundColor?: string;
}

// === API Functions ===

// Blog Posts
const fetchBlogPosts = (
  skip: number,
  take: number,
  searchPhrase: string = "",
): AxiosPromise<BaseApiPaging<BlogPostListItemApi>> =>
  api.get<BaseApiPaging<BlogPostListItemApi>>(endpoints.SHOP.BLOG.GET(skip, take, searchPhrase));

const fetchBlogPost = (id: number): AxiosPromise<BlogPostApi> =>
  api.get<BlogPostApi>(endpoints.SHOP.BLOG.GET_BY_ID(id));

const createBlogPost = (data: BlogPostFormValues): AxiosPromise<{ id: number }> =>
  api.post<{ id: number }>(endpoints.SHOP.BLOG.CREATE(), data);

const updateBlogPost = (id: number, data: BlogPostFormValues): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.BLOG.UPDATE(id), data);

const deleteBlogPost = (id: number): Promise<void> => api.delete(endpoints.SHOP.BLOG.DELETE(id));

const duplicateBlogPost = (id: number): AxiosPromise<{ id: number }> =>
  api.post<{ id: number }>(endpoints.SHOP.BLOG.DUPLICATE(id));

// Blog Sections
const fetchBlogSections = (blogPostId: number): AxiosPromise<BlogPostSectionApi[]> =>
  api.get<BlogPostSectionApi[]>(endpoints.SHOP.BLOG.SECTIONS.GET(blogPostId));

const createBlogSection = (blogPostId: number, data: SectionFormValues): AxiosPromise<number> =>
  api.post<number>(endpoints.SHOP.BLOG.SECTIONS.CREATE(blogPostId), data);

const updateBlogSection = (blogPostId: number, sectionId: number, data: SectionFormValues): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.BLOG.SECTIONS.UPDATE(blogPostId, sectionId), data);

const deleteBlogSection = (blogPostId: number, sectionId: number): Promise<void> =>
  api.delete(endpoints.SHOP.BLOG.SECTIONS.DELETE(blogPostId, sectionId));

const reorderBlogSections = (blogPostId: number, sectionIds: number[]): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.BLOG.SECTIONS.REORDER(blogPostId), { sectionIds });

// Blog Categories
const fetchBlogCategories = (): AxiosPromise<BlogCategoryApi[]> =>
  api.get<BlogCategoryApi[]>(endpoints.SHOP.BLOG.CATEGORIES.GET());

const createBlogCategory = (data: BlogCategoryFormValues): AxiosPromise<{ id: number }> =>
  api.post<{ id: number }>(endpoints.SHOP.BLOG.CATEGORIES.CREATE(), data);

const updateBlogCategory = (id: number, data: { name: string; slug: string }): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.BLOG.CATEGORIES.UPDATE(id), data);

const deleteBlogCategory = (id: number): Promise<void> => api.delete(endpoints.SHOP.BLOG.CATEGORIES.DELETE(id));

// Blog Settings
const fetchBlogSettings = (): AxiosPromise<BlogSettingsApi> =>
  api.get<BlogSettingsApi>(endpoints.SHOP.BLOG.SETTINGS.GET());

const updateBlogSettings = (data: BlogSettingsApi): AxiosPromise<void> =>
  api.put<void>(endpoints.SHOP.BLOG.SETTINGS.UPDATE(), data);

export const blogApi = {
  fetchBlogPosts,
  fetchBlogPost,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  duplicateBlogPost,
  fetchBlogSections,
  createBlogSection,
  updateBlogSection,
  deleteBlogSection,
  reorderBlogSections,
  fetchBlogCategories,
  createBlogCategory,
  updateBlogCategory,
  deleteBlogCategory,
  fetchBlogSettings,
  updateBlogSettings,
};
